import { useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { InfoIcon, ArrowSquareOutIcon } from '@phosphor-icons/react'
import { ScoopText, ScoopChip, ScoopTooltip, ScoopButton, ScoopColumn } from '../../../../ui'
import styles from './ClusterVisualization.module.scss'

// Build treemap chart option for clusters - more stable than force graph
const buildTreemapOption = (clusters) => {
    if (!clusters || clusters.length === 0) return null

    // Calculate total count for percentage calculations
    const totalCount = clusters.reduce((sum, cluster) => sum + cluster.count, 0)

    // Transform clusters to treemap data
    const data = clusters.map((cluster, index) => ({
        name: cluster.clusterName,
        value: cluster.count,
        percentage: ((cluster.count / totalCount) * 100).toFixed(1),
        clusterID: cluster.clusterID,
        description: cluster.clusterDescription,
        attributes: cluster.attributes,
        queryFilter: cluster.queryFilter,
        itemStyle: {
            color: getClusterColor(index),
            borderColor: '#fff',
            borderWidth: 2,
        },
        label: {
            show: true,
            formatter: (params) => {
                // Show name and percentage
                return `{name|${params.data.name}}\n{percent|${params.data.percentage}%}`
            },
            rich: {
                name: {
                    fontSize: 11,
                    fontWeight: 'bold',
                    color: '#fff',
                    lineHeight: 15,
                },
                percent: {
                    fontSize: 10,
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: 12,
                },
            },
            position: 'inside',
        },
        emphasis: {
            itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.3)',
                borderColor: '#fff',
                borderWidth: 3,
            },
        },
    }))

    return {
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                return `<div style="padding: 8px; max-width: 250px; word-wrap: break-word; white-space: normal;">
                    <strong style="display: block; margin-bottom: 4px; line-height: 1.3;">${params.data.name}</strong>
                    <div style="font-size: 12px;">
                        Count: ${(params.data.value ?? 0).toLocaleString()}<br/>
                        Percentage: ${params.data.percentage}%
                    </div>
                    <div style="margin-top: 4px; font-size: 11px; color: #888;">
                        Click to see details
                    </div>
                </div>`
            },
            confine: true, // Keep tooltip within chart bounds
            extraCssText: 'max-width: 250px; white-space: normal; word-break: break-word;',
        },
        series: [
            {
                type: 'treemap',
                width: '100%',
                height: '100%',
                roam: false,
                nodeClick: 'zoomToNode',
                breadcrumb: {
                    show: false,
                },
                levels: [
                    {
                        itemStyle: {
                            borderWidth: 0,
                            gapWidth: 2,
                        },
                    },
                    {
                        itemStyle: {
                            borderWidth: 2,
                            borderColor: '#fff',
                            gapWidth: 2,
                        },
                    },
                ],
                data: data,
            },
        ],
    }
}

// Get color for cluster based on index
const getClusterColor = (index) => {
    const colors = [
        '#5470c6', // Blue
        '#91cc75', // Green
        '#fac858', // Yellow
        '#ee6666', // Red
        '#73c0de', // Light Blue
        '#3ba272', // Dark Green
        '#fc8452', // Orange
        '#9a60b4', // Purple
        '#ea7ccc', // Pink
    ]
    return colors[index % colors.length]
}

// Helper to get cluster color based on clusterID
const getClusterColorById = (clusters, clusterID) => {
    const index = clusters.findIndex((c) => c.clusterID === clusterID)
    return getClusterColor(index >= 0 ? index : 0)
}

export const ClusterVisualization = ({
    clusterData,
    isPublic,
    isEmbedded,
    selectedInbox,
    setSelectedTab,
    mlConfig,
    setMlConfig,
    setMLResults,
    isTourWorkspace,
}) => {
    const [selectedCluster, setSelectedCluster] = useState(null)

    // Extract cluster information from the response
    const clusters = clusterData?.clusters
    const numInstances = clusterData?.numInstances
    const columns = clusterData?.columns

    if (!clusters || clusters.length === 0) {
        return null
    }

    // Handle opening cluster results in Data Science Studio
    const handleOpenInStudio = () => {
        if (setSelectedTab && setMLResults && mlConfig && setMlConfig) {
            // Set the ML results with cluster data
            setMLResults(clusterData)

            // Configure ML config for cluster model
            const updatedMlConfig = { ...mlConfig }
            updatedMlConfig.modelType = 'cluster'

            // Preserve filter information if available
            if (clusterData.filter) {
                updatedMlConfig.filter = clusterData.filter
                updatedMlConfig.filterDescription = clusterData.filterDescription
            }
            if (clusterData.filterDate) {
                updatedMlConfig.filterDate = clusterData.filterDate
            }

            setMlConfig(updatedMlConfig)

            // Switch to Data Science Studio tab
            setSelectedTab('machine-learning')
        }
    }

    // Build chart option
    const chartOption = buildTreemapOption(clusters)

    // Handle cluster click
    const onChartEvents = {
        click: (params) => {
            setSelectedCluster(params.data)
        },
    }

    return (
        <ScoopColumn className={styles.container}>
            {/* Title and metadata */}
            <div className={styles.titleBox}>
                <ScoopText className={styles.title}>
                    Data Segmentation Analysis
                    <ScoopTooltip title="Machine learning has identified distinct groups within your data based on common patterns and characteristics">
                        <InfoIcon size={14} className={styles.infoIcon} />
                    </ScoopTooltip>
                </ScoopText>

                <ScoopText className={styles.subtitle}>
                    {numInstances?.toLocaleString() || 'N/A'} records • {clusters.length} clusters
                    found
                </ScoopText>
            </div>

            {/* Treemap chart */}
            <div className={styles.chartBox}>
                {chartOption && (
                    <ReactECharts
                        className={styles.chart}
                        option={chartOption}
                        notMerge={true}
                        lazyUpdate={true}
                        onEvents={onChartEvents}
                    />
                )}
            </div>

            {/* Action buttons - hide in public/embedded/tour mode */}
            {!isPublic && !isEmbedded && !isTourWorkspace && setSelectedTab && (
                <div className={styles.actionsContainer}>
                    <ScoopButton
                        size="xs"
                        variant="outlineGray"
                        onClick={handleOpenInStudio}
                        leftSection={<ArrowSquareOutIcon size={16} />}
                    >
                        Open in Data Science Studio
                    </ScoopButton>
                </div>
            )}

            {/* Selected cluster details - only show when selected */}
            {selectedCluster && (
                <div className={styles.selectedClusterBox}>
                    <div
                        className={styles.selectedClusterPaper}
                        style={{
                            '--cluster-color': getClusterColorById(
                                clusters,
                                selectedCluster.clusterID
                            ),
                        }}
                    >
                        <ScoopText className={styles.selectedClusterTitle}>
                            {selectedCluster.name}
                        </ScoopText>

                        <ScoopText className={styles.selectedClusterCount}>
                            {selectedCluster.value?.toLocaleString()} records (
                            {selectedCluster.percentage}%)
                        </ScoopText>

                        <ScoopText className={styles.selectedClusterDescription}>
                            {selectedCluster.description}
                        </ScoopText>

                        {selectedCluster.attributes && selectedCluster.attributes.length > 0 && (
                            <div>
                                <ScoopText className={styles.attributesLabel}>
                                    Key Attributes:
                                </ScoopText>
                                <div className={styles.attributesBox}>
                                    {selectedCluster.attributes.map((attr, idx) => (
                                        <ScoopChip key={idx} label={attr} size="small" />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Compact cluster list */}
            <div className={styles.clusterListBox}>
                <ScoopText className={styles.clusterListTitle}>
                    All Clusters (click chart or items below for details)
                </ScoopText>
                <div className={styles.clustersContainer}>
                    {clusters.map((cluster, index) => (
                        <div
                            key={cluster.clusterID}
                            className={`${styles.clusterItem} ${
                                selectedCluster?.clusterID === cluster.clusterID
                                    ? styles.selected
                                    : styles.unselected
                            }`}
                            style={{
                                '--cluster-color': getClusterColor(index),
                            }}
                            onClick={() =>
                                setSelectedCluster({
                                    name: cluster.clusterName,
                                    value: cluster.count,
                                    percentage: ((cluster.count / numInstances) * 100).toFixed(1),
                                    description: cluster.clusterDescription,
                                    attributes: cluster.attributes,
                                    clusterID: cluster.clusterID,
                                })
                            }
                        >
                            <div className={styles.clusterItemInner}>
                                <ScoopText className={styles.clusterName}>
                                    {cluster.clusterName}
                                </ScoopText>
                                <ScoopText className={styles.clusterCount}>
                                    {(cluster.count ?? 0).toLocaleString()} (
                                    {(((cluster.count ?? 0) / (numInstances ?? 1)) * 100).toFixed(
                                        0
                                    )}
                                    %)
                                </ScoopText>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </ScoopColumn>
    )
}
